import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, animate } from "framer-motion";

interface CountBadgeProps {
  count: number;
  /** Collapsed sidebar: a small badge pinned to the icon instead of the full pill. */
  dot?: boolean;
}

// A slow, low-amplitude glow that runs the whole time the badge is on
// screen — the "still alive" cue for when nothing is actually changing.
const badgeBreathe = {
  boxShadow: [
    "0 1px 3px rgba(0,0,0,0.35), 0 0 0px 0px rgba(220,38,38,0.55)",
    "0 1px 3px rgba(0,0,0,0.35), 0 0 5px 1.5px rgba(220,38,38,0.55)",
    "0 1px 3px rgba(0,0,0,0.35), 0 0 0px 0px rgba(220,38,38,0.55)",
  ],
};
const dotBreathe = {
  boxShadow: [
    "0 1px 2px rgba(0,0,0,0.4), 0 0 0px 0px rgba(220,38,38,0.6)",
    "0 1px 2px rgba(0,0,0,0.4), 0 0 4px 1px rgba(220,38,38,0.6)",
    "0 1px 2px rgba(0,0,0,0.4), 0 0 0px 0px rgba(220,38,38,0.6)",
  ],
};
const breatheTransition = {
  duration: 2.6,
  repeat: Infinity,
  ease: "easeInOut" as const,
};

/**
 * Sidebar notification count. Springs in when a count first appears, holds a
 * quiet breathing glow so it still reads as "live" while idle, tweens the
 * number smoothly to its new value instead of jumping, and adds a single
 * outward ring pulse the moment the count goes up.
 *
 * Layered on purpose: the outer span only animates plain scalars (its
 * enter/exit spring), the fill span only animates the keyframe glow loop —
 * mixing the two on one element makes Motion's spring/keyframe resolution
 * unreliable. The pulse ring is a fresh element per bump (remounted via
 * `key`) so it always plays its one-shot animation cleanly.
 */
const CountBadge = ({ count, dot = false }: CountBadgeProps) => {
  const previousCount = useRef(count);
  const [displayed, setDisplayed] = useState(count);
  const [pulseKey, setPulseKey] = useState(0);

  useEffect(() => {
    const from = previousCount.current;
    if (count > from) setPulseKey((k) => k + 1); // only announce increases
    const controls = animate(from, count, {
      duration: 0.5,
      ease: "easeOut",
      onUpdate: (v) => setDisplayed(Math.round(v)),
    });
    previousCount.current = count;
    return () => controls.stop();
  }, [count]);

  const display = displayed > 99 ? "99+" : displayed;

  return (
    <AnimatePresence initial={false}>
      {count > 0 &&
        (dot ? (
          <motion.span
            key="dot"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 520, damping: 30 }}
            className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5"
          >
            <motion.span
              animate={dotBreathe}
              transition={breatheTransition}
              className="absolute inset-0 rounded-full bg-gradient-to-b from-[#ef4444] to-[#b91c1c]"
            />
            {pulseKey > 0 && (
              <motion.span
                key={pulseKey}
                initial={{ scale: 1, opacity: 0.6 }}
                animate={{ scale: 2.2, opacity: 0 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
                className="absolute inset-0 rounded-full bg-red-500"
              />
            )}
          </motion.span>
        ) : (
          <motion.span
            key="badge"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 460, damping: 28 }}
          >
            <motion.span
              animate={badgeBreathe}
              transition={breatheTransition}
              className="relative inline-flex items-center justify-center min-w-4.75 h-4.75 px-1.5 rounded-full bg-gradient-to-b from-[#ef4444] to-[#b91c1c]"
            >
              {pulseKey > 0 && (
                <motion.span
                  key={pulseKey}
                  initial={{ scale: 1, opacity: 0.55 }}
                  animate={{ scale: 1.9, opacity: 0 }}
                  transition={{ duration: 0.7, ease: "easeOut" }}
                  className="absolute inset-0 rounded-full bg-red-500"
                />
              )}
              <span className="relative text-white text-[10px] font-bold leading-none tabular-nums">
                {display}
              </span>
            </motion.span>
          </motion.span>
        ))}
    </AnimatePresence>
  );
};

export default CountBadge;
